import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Music, Plus, Trash2, Check, X, GripVertical, Upload, Play, Pause } from "lucide-react";
import { toast } from "sonner";
import {getAuthHeaders, hasToken, BACKEND_URL} from "@/lib/api";

const API = `${BACKEND_URL}/api`;

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

export default function EventPlaylist({ eventId, isOwner, userId }) {
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const fileInputRef = useRef(null);
  const audioRefs = useRef({});
  const [newSong, setNewSong] = useState({
    title: "",
    artist: "",
    album: "",
    spotify_url: "",
    added_by_name: "Gast"
  });

  useEffect(() => {
    fetchPlaylist();
  }, [eventId]);

  const fetchPlaylist = async () => {
    try {
      const response = await axios.get(`${API}/playlists/events/${eventId}/playlist`);
      setPlaylist(response.data);
    } catch (error) {
      console.error("Failed to fetch playlist:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSong = async (e) => {
    e.preventDefault();
    
    if (!newSong.title || !newSong.artist) {
      toast.error("Titel und Künstler sind erforderlich");
      return;
    }

    try {
      await axios.post(
        `${API}/playlists/events/${eventId}/playlist/songs`,
        newSong,
        authConfig()
      );
      
      toast.success(
        playlist?.mode === "approval" 
          ? "Song zur Genehmigung eingereicht!" 
          : "Song hinzugefügt!"
      );
      
      setNewSong({ title: "", artist: "", album: "", spotify_url: "", added_by_name: "Gast" });
      setShowAddForm(false);
      fetchPlaylist();
    } catch (error) {
      console.error("Failed to add song:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    }
  };

  // Upload audio file as song
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error("Datei ist zu groß (max. 50MB)");
      return;
    }

    if (!file.type.startsWith("audio/")) {
      toast.error("Bitte nur Audiodateien hochladen");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("added_by_name", newSong.added_by_name || "Gast");

    try {
      await axios.post(
        `${API}/playlists/events/${eventId}/playlist/upload`,
        formData,
        {
          ...authConfig(),
          headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
        }
      );
      toast.success("Lied hochgeladen! 🎵");
      fetchPlaylist();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const togglePlay = (songId, audioUrl) => {
    if (!audioUrl) return;
    
    const audio = audioRefs.current[songId];
    if (!audio) return;

    if (playingId === songId) {
      audio.pause();
      setPlayingId(null);
    } else {
      if (playingId && audioRefs.current[playingId]) {
        audioRefs.current[playingId].pause();
      }
      audio.play();
      setPlayingId(songId);
    }
  };

  const handleModeChange = async (newMode) => {
    try {
      await axios.patch(
        `${API}/playlists/events/${eventId}/playlist/mode`,
        { mode: newMode },
        authConfig()
      );
      toast.success("Playlist-Modus aktualisiert");
      fetchPlaylist();
    } catch (error) {
      console.error("Failed to update mode:", error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleApproveSong = async (songId, status) => {
    try {
      await axios.patch(
        `${API}/playlists/events/${eventId}/playlist/songs/${songId}/approve`,
        { status },
        authConfig()
      );
      toast.success(status === "approved" ? "Song genehmigt" : "Song abgelehnt");
      fetchPlaylist();
    } catch (error) {
      console.error("Failed to approve song:", error);
      toast.error("Fehler bei der Genehmigung");
    }
  };

  const handleDeleteSong = async (songId) => {
    if (!confirm("Song wirklich löschen?")) return;
    
    try {
      await axios.delete(
        `${API}/playlists/events/${eventId}/playlist/songs/${songId}`,
        authConfig()
      );
      toast.success("Song gelöscht");
      fetchPlaylist();
    } catch (error) {
      console.error("Failed to delete song:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const songs = playlist?.songs || [];
  const mode = playlist?.mode || "open";
  const canAddSongs = mode === "open" || mode === "approval";

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Music className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Playlist</h3>
        </div>
        
        {isOwner && (
          <Select value={mode} onValueChange={handleModeChange}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Offen für alle</SelectItem>
              <SelectItem value="approval">Mit Genehmigung</SelectItem>
              <SelectItem value="owner_only">Nur Ersteller</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Mode Info */}
      <div className="mb-4 p-3 bg-secondary/30 rounded-lg text-sm text-muted-foreground">
        {mode === "open" && "🎵 Alle Gäste können Songs hinzufügen"}
        {mode === "approval" && "✓ Songs müssen vom Ersteller genehmigt werden"}
        {mode === "owner_only" && "🔒 Nur der Ersteller kann Songs hinzufügen"}
      </div>

      {/* Songs List */}
      <div className="space-y-3 mb-6">
        {songs.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Music className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Noch keine Songs in der Playlist</p>
          </div>
        )}
        
        {songs.map((song) => (
          <div
            key={song.id}
            className="flex items-center gap-3 p-3 bg-secondary/20 rounded-lg hover:bg-secondary/30 transition-colors"
          >
            {/* Play button if audio file exists */}
            {song.audio_url ? (
              <button
                onClick={() => togglePlay(song.id, song.audio_url)}
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  playingId === song.id ? "bg-primary text-white" : "bg-secondary"
                }`}
              >
                {playingId === song.id ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4 ml-0.5" />
                )}
              </button>
            ) : (
              <Music className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            )}
            
            {/* Hidden audio element */}
            {song.audio_url && (
              <audio
                ref={(el) => audioRefs.current[song.id] = el}
                src={`${BACKEND_URL}${song.audio_url}`}
                onEnded={() => setPlayingId(null)}
              />
            )}
            
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{song.title}</p>
              <p className="text-sm text-muted-foreground truncate">{song.artist}</p>
              {song.status === "pending" && (
                <span className="text-xs text-amber-500">⏳ Wartet auf Genehmigung</span>
              )}
            </div>

            {isOwner && song.status === "pending" && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={() => handleApproveSong(song.id, "approved")}
                >
                  <Check className="w-4 h-4 text-green-500" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={() => handleApproveSong(song.id, "rejected")}
                >
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            )}

            {isOwner && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={() => handleDeleteSong(song.id)}
              >
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Add Song Button/Form */}
      {(canAddSongs || isOwner) && (
        <>
          {!showAddForm ? (
            <div className="space-y-3">
              <Button
                onClick={() => setShowAddForm(true)}
                className="w-full"
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Song manuell hinzufügen
              </Button>
              
              {/* File Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
                variant="outline"
                disabled={uploading}
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Wird hochgeladen..." : "Audiodatei hochladen"}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleAddSong} className="space-y-4 p-4 border rounded-lg">
              <div>
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={newSong.title}
                  onChange={(e) => setNewSong({ ...newSong, title: e.target.value })}
                  placeholder="z.B. Perfect"
                  required
                />
              </div>

              <div>
                <Label htmlFor="artist">Künstler *</Label>
                <Input
                  id="artist"
                  value={newSong.artist}
                  onChange={(e) => setNewSong({ ...newSong, artist: e.target.value })}
                  placeholder="z.B. Ed Sheeran"
                  required
                />
              </div>

              <div>
                <Label htmlFor="album">Album (optional)</Label>
                <Input
                  id="album"
                  value={newSong.album}
                  onChange={(e) => setNewSong({ ...newSong, album: e.target.value })}
                  placeholder="z.B. Divide"
                />
              </div>

              <div>
                <Label htmlFor="spotify">Spotify URL (optional)</Label>
                <Input
                  id="spotify"
                  value={newSong.spotify_url}
                  onChange={(e) => setNewSong({ ...newSong, spotify_url: e.target.value })}
                  placeholder="https://open.spotify.com/..."
                />
              </div>

              <div>
                <Label htmlFor="name">Dein Name</Label>
                <Input
                  id="name"
                  value={newSong.added_by_name}
                  onChange={(e) => setNewSong({ ...newSong, added_by_name: e.target.value })}
                  placeholder="Name"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  Hinzufügen
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewSong({ title: "", artist: "", album: "", spotify_url: "", added_by_name: "Gast" });
                  }}
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          )}
        </>
      )}
    </Card>
  );
}
